import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Download, Loader2, Search, RotateCcw, FileText, BarChart3, BookOpen, FileSpreadsheet } from 'lucide-react';
import { COMPANY_INFO } from '../../constants/companyInfo';

// ═══════════════════════════════════════════════════════════════
// MERGED REPORTS COMPONENT: Daybook + Account Statement + Trial Balance
// ═══════════════════════════════════════════════════════════════
const ReportsPanel = () => {
    const [activeReport, setActiveReport] = useState('trial_balance');

    // --- DAYBOOK STATES ---
    const [daybookLoading, setDaybookLoading] = useState(false);
    const today = new Date().toISOString().split('T')[0];
    const [daybookDates, setDaybookDates] = useState({ start: today, end: today });

    // --- ACCOUNT STATEMENT STATES ---
    const [stmtLoading, setStmtLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [stmtDates, setStmtDates] = useState({ 
        from: '', 
        to: new Date().toISOString().split('T')[0] 
    });
    const [reportData, setReportData] = useState({ accountInfo: null, openingBalance: 0, transactions: [] });
    const dropdownRef = useRef(null);

    // --- TRIAL BALANCE STATES ---
    const [tbLoading, setTbLoading] = useState(false);
    const [tbDates, setTbDates] = useState({ 
        from: '', 
        to: new Date().toISOString().split('T')[0] 
    });
    const [tbData, setTbData] = useState({ hierarchy: {}, summary: null });

    // ═══════════════════════════════════════════════════════════
    // COMMON HELPERS
    // ═══════════════════════════════════════════════════════════
    const formatAmount = (val) => {
        const num = Number(val);
        if (isNaN(num) || num === 0) return '0.00';
        return Math.abs(num).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // ═══════════════════════════════════════════════════════════
    // DAYBOOK FUNCTIONS (UNCHANGED)
    // ═══════════════════════════════════════════════════════════
    const generateDaybookPDF = async () => {
        setDaybookLoading(true);
        try {
            const response = await API.get(`/v1/rep/daybook-report`, {
                params: { startDate: daybookDates.start, endDate: daybookDates.end }
            });

            const { transactions, openingCashBalance, closingCashBalance, summary } = response.data || {};

            if (!transactions || transactions.length === 0) {
                alert("No transactions found for the selected date range!");
                setDaybookLoading(false);
                return;
            }

            const doc = new jsPDF('l', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text(COMPANY_INFO?.name || "UNITED MOTORS", pageWidth / 2, 12, { align: 'center' });

            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.text(`From : ${daybookDates.start.split('-').reverse().join('/')}`, pageWidth - 45, 12);
            doc.text(`To : ${daybookDates.end.split('-').reverse().join('/')}`, pageWidth - 45, 17);

            doc.setLineWidth(0.4);
            doc.line(15, 22, pageWidth - 15, 22);

            doc.setFont("helvetica", "bold");
            doc.text(`Opening Cash Balance :`, pageWidth - 90, 28);
            doc.text(`${formatAmount(openingCashBalance)}`, pageWidth - 15, 28, { align: 'right' });

            const rows = [];
            let currentVoucherType = '';

            transactions.forEach((tx) => {
                if (tx.voucher_type !== currentVoucherType) {
                    currentVoucherType = tx.voucher_type;
                    rows.push([
                        { 
                            content: currentVoucherType ? currentVoucherType.toUpperCase() : 'GENERAL', 
                            colSpan: 8, 
                            styles: { fontStyle: 'bold', fillColor: [240, 240, 240], textColor: [0, 0, 0] } 
                        }
                    ]);
                }

                const formattedBalance = `${formatAmount(tx.balance)} ${tx.balanceType || ''}`;

                rows.push([
                    tx.v_no || '',
                    tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '',
                    tx.code || '',
                    tx.account_title || '',
                    Number(tx.debit || 0) ? formatAmount(tx.debit) : '0.00',
                    Number(tx.credit || 0) ? formatAmount(tx.credit) : '0.00',
                    formattedBalance,
                    tx.remarks || ''
                ]);
            });

            autoTable(doc, {
                startY: 32,
                head: [[
                    { content: 'V. No', styles: { halign: 'left' } },
                    { content: 'Date', styles: { halign: 'left' } },
                    { content: 'Account Number', styles: { halign: 'left' } },
                    { content: 'Title Of A/c', styles: { halign: 'left' } },
                    { content: 'Debit', styles: { halign: 'right' } },
                    { content: 'Credit', styles: { halign: 'right' } },
                    { content: 'Bal Amt', styles: { halign: 'right' } },
                    { content: 'Remarks', styles: { halign: 'left' } }
                ]],
                body: rows,
                theme: 'plain',
                headStyles: { fontSize: 8.5, fontStyle: 'bold', textColor: [0, 0, 0], lineWidth: { bottom: 0.4 }, lineColor: [0, 0, 0] },
                styles: { fontSize: 8, cellPadding: 1.5, textColor: [0, 0, 0] },
                margin: { left: 15, right: 15 }
            });

            const finalY = doc.lastAutoTable.finalY + 6;
            doc.setLineWidth(0.3);
            doc.line(15, finalY, pageWidth - 15, finalY);

            doc.setFont("helvetica", "bold");
            doc.text("Grand Total:", 105, finalY + 6);
            doc.text(formatAmount(summary?.totalDebit), 155, finalY + 6, { align: 'right' });
            doc.text(formatAmount(summary?.totalCredit), 180, finalY + 6, { align: 'right' });

            doc.rect(pageWidth - 95, finalY + 12, 80, 8);
            doc.text("Cash Closing Bal:", pageWidth - 92, finalY + 17);
            doc.text(formatAmount(closingCashBalance), pageWidth - 18, finalY + 17, { align: 'right' });

            doc.save(`Daybook_${daybookDates.start}_to_${daybookDates.end}.pdf`);
        } catch (err) {
            console.error("Export Error:", err);
            alert("Failed to export PDF.");
        } finally {
            setDaybookLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════
    // ACCOUNT STATEMENT FUNCTIONS (UNCHANGED)
    // ═══════════════════════════════════════════════════════════
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchTerm.length > 0 && isOpen) {
                try {
                    const res = await API.get(`/accounts/search?search=${searchTerm}&limit=10`);
                    setOptions(res.data?.data || []);
                } catch (err) { console.error("Search Error:", err); }
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, isOpen]);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSetOpeningDate = () => {
        if (!selectedAccount) return alert("Please select an account first.");
        const rawDate = selectedAccount.created_at || selectedAccount.CreatedAt; 
        if (rawDate) {
            const openingDate = new Date(rawDate).toISOString().split('T')[0];
            setStmtDates(prev => ({ ...prev, from: openingDate }));
        } else {
            alert("Account opening date not found in database.");
        }
    };

    const fetchStatement = async () => {
        if (!selectedAccount) return alert("Please select an account.");
        if (!stmtDates.from) return alert("Please select 'From Date'");

        setStmtLoading(true);
        try {
            const res = await API.get(`v1/rep/ledger?account_id=${selectedAccount.id}&from_date=${stmtDates.from}&to_date=${stmtDates.to}`);
            if (res.data?.success) {
                setReportData({ 
                    accountInfo: res.data.accountInfo || null, 
                    openingBalance: res.data.openingBalance || 0, 
                    transactions: res.data.data || [] 
                });
            }
        } catch (err) {
            alert("Error loading account statement.");
        } finally { setStmtLoading(false); }
    };

    const formatBalance = (val) => {
        const num = Number(val);
        if (isNaN(num) || num === 0) return "0.00";
        return num < 0 
            ? `${Math.abs(num).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} CR` 
            : `${num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} DR`;
    };

    const exportStatementPDF = () => {
        if (!reportData?.accountInfo) return alert("No data to export!");

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(22, 160, 133); 
        doc.text(COMPANY_INFO?.name || "UNITED MOTORS", 15, 20);

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(COMPANY_INFO?.tagline || "", 15, 25);

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`${COMPANY_INFO?.address || ""}`, 15, 32);
        doc.text(`Phone: ${COMPANY_INFO?.phone || ""} | Email: ${COMPANY_INFO?.email || ""}`, 15, 37);

        doc.setDrawColor(22, 160, 133);
        doc.setLineWidth(0.5);
        doc.line(15, 42, pageWidth - 15, 42);

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text("ACCOUNT STATEMENT / LEDGER", 15, 52);

        doc.setFontSize(10);
        doc.text(`Account: ${reportData.accountInfo.title || ''} (ID: ${reportData.accountInfo.id || ''})`, 15, 60);
        doc.text(`Period: ${stmtDates.from} to ${stmtDates.to}`, pageWidth - 15, 60, { align: 'right' });

        let currentBal = Number(reportData.openingBalance || 0);
        const tableBody = [
            ['-', 'Opening Balance Brought Forward', '-', '-', { content: formatBalance(currentBal), styles: { fontStyle: 'bold' } }]
        ];

        (reportData.transactions || []).forEach(t => {
            currentBal += (Number(t.debit || 0) - Number(t.credit || 0));
            tableBody.push([
                t.created_at ? new Date(t.created_at).toLocaleDateString() : '',
                t.description || '',
                (t.debit || 0) > 0 ? formatAmount(t.debit) : '-',
                (t.credit || 0) > 0 ? formatAmount(t.credit) : '-',
                formatBalance(currentBal)
            ]);
        });

        autoTable(doc, {
            startY: 68,
            head: [['DATE', 'DESCRIPTION', 'DEBIT', 'CREDIT', 'BALANCE']],
            body: tableBody,
            theme: 'grid',
            headStyles: { fillColor: [44, 62, 80], fontSize: 9 },
            columnStyles: {
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'right' }
            },
            styles: { fontSize: 8 }
        });

        doc.save(`Ledger_${reportData.accountInfo.title || 'Account'}_${stmtDates.from}.pdf`);
    };

    // ═══════════════════════════════════════════════════════════
    // TRIAL BALANCE FUNCTIONS (UPDATED FORMAT, COLORS & NO LABELS)
    // ═══════════════════════════════════════════════════════════

    const toArray = (val) => {
        if (val === null || val === undefined) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === 'object') return Object.values(val);
        return [];
    };

    const fetchTrialBalance = async () => {
        setTbLoading(true);
        try {
            const params = {};
            if (tbDates.from) params.from_date = tbDates.from;
            if (tbDates.to) params.to_date = tbDates.to;

            const res = await API.get('/v1/rep/detailed-trial-balance', { params });

            if (res.data?.success) {
                setTbData({
                    hierarchy: res.data.hierarchy || {},
                    summary: res.data.summary || null
                });
            } else {
                alert(res.data?.message || "Failed to load Trial Balance data.");
            }
        } catch (err) {
            console.error("Trial Balance Error:", err);
            alert("Failed to load Trial Balance data.");
        } finally {
            setTbLoading(false);
        }
    };

    // ✅ UI RENDER ROW COMPONENT
    const TrialBalanceRow = ({ data, level = 0 }) => {
        if (!data) return null;

        const indent = level * 16;
        const bgColors = ['#f1f5f9', '#f8fafc', '#ffffff', '#ffffff'];
        const textColors = ['#0f172a', '#1e293b', '#334155', '#475569'];
        const fontWeights = ['bold', 'bold', '600', 'normal'];
        const fontSizes = ['13px', '12px', '11.5px', '11px'];

        const isAccount = level === 3;
        const bg = bgColors[Math.min(level, 3)];
        const color = textColors[Math.min(level, 3)];
        const fontWeight = fontWeights[Math.min(level, 3)];
        const fontSize = fontSizes[Math.min(level, 3)];

        const id = data.HeadID || data.SubHeadID || data.SubSubHeadID || data.id || '';
        const name = data.HeadName || data.SubHeadName || data.SubSubHeadName || data.name || data.title || '';

        const totals = data.totals || {};
        const opDr = totals.opDr || totals.opening_debit || data.opening_debit || 0;
        const opCr = totals.opCr || totals.opening_credit || data.opening_credit || 0;
        const curDr = totals.curDr || totals.current_debit || data.current_debit || 0;
        const curCr = totals.curCr || totals.current_credit || data.current_credit || 0;
        const clsDr = totals.clsDr || totals.closing_debit || data.closing_debit || 0;
        const clsCr = totals.clsCr || totals.closing_credit || data.closing_credit || 0;

        return (
            <>
                <tr style={{ 
                    backgroundColor: bg, 
                    color: color, 
                    fontWeight: fontWeight,
                    fontSize: fontSize,
                    borderBottom: level === 0 ? '2px solid #cbd5e1' : '1px solid #f1f5f9'
                }}>
                    <td style={{ ...styles.td, textAlign: 'center', paddingLeft: `${10 + indent}px` }}>
                        {id}
                    </td>
                    <td style={{ ...styles.td, paddingLeft: `${15 + indent}px` }}>
                        {name}
                    </td>
                    <td style={styles.tdRight}>{formatAmount(opDr)}</td>
                    <td style={styles.tdRight}>{formatAmount(opCr)}</td>
                    <td style={styles.tdRight}>{formatAmount(curDr)}</td>
                    <td style={styles.tdRight}>{formatAmount(curCr)}</td>
                    <td style={styles.tdRight}>{formatAmount(clsDr)}</td>
                    <td style={styles.tdRight}>{formatAmount(clsCr)}</td>
                </tr>

                {/* Recursively render children */}
                {level < 3 && toArray(data.subHeads || data.subSubHeads || data.accounts).map((child, idx) => (
                    <TrialBalanceRow key={`${level}-${idx}`} data={child} level={level + 1} />
                ))}
            </>
        );
    };

  const exportTrialBalancePDF = () => {
        if (!tbData?.summary || !tbData.hierarchy) {
            return alert("No Trial Balance data available to export!");
        }

        const hierarchyEntries = toArray(tbData.hierarchy);
        if (hierarchyEntries.length === 0) {
            return alert("No Trial Balance data available to export!");
        }

        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();

        // Professional Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(15, 23, 42);
        doc.text(COMPANY_INFO?.name || "UNITED MOTORS", pageWidth / 2, 12, { align: 'center' });

        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.text("TRIAL BALANCE REPORT", pageWidth / 2, 18, { align: 'center' });

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const periodText = tbDates.from ? `Period: ${tbDates.from} to ${tbDates.to}` : `As of: ${tbDates.to}`;
        doc.text(periodText, pageWidth / 2, 23, { align: 'center' });

        doc.setLineWidth(0.5);
        doc.setDrawColor(203, 213, 225); // ✅ Fixed: setColor to setDrawColor
        doc.line(14, 26, pageWidth - 14, 26);

        const rows = [];

        // ✅ RECURSIVE PDF GENERATION (NO LABELS, ATTRACTIVE COLORS)
        const addRows = (data, level = 0) => {
            if (!data) return;

            const indent = '    '.repeat(level);
            const id = data.HeadID || data.SubHeadID || data.SubSubHeadID || data.id || '';
            const name = data.HeadName || data.SubHeadName || data.SubSubHeadName || data.name || data.title || '';

            const totals = data.totals || {};
            const opDr = totals.opDr || totals.opening_debit || data.opening_debit || 0;
            const opCr = totals.opCr || totals.opening_credit || data.opening_credit || 0;
            const curDr = totals.curDr || totals.current_debit || data.current_debit || 0;
            const curCr = totals.curCr || totals.current_credit || data.current_credit || 0;
            const clsDr = totals.clsDr || totals.closing_debit || data.closing_debit || 0;
            const clsCr = totals.clsCr || totals.closing_credit || data.closing_credit || 0;

            const bgPalette = [
                [241, 245, 249], // Level 0 (Head)
                [248, 250, 252], // Level 1 (SubHead)
                [255, 255, 255], // Level 2 (SubSubHead)
                [255, 255, 255]  // Level 3 (Account)
            ];
            const textPalette = [
                [15, 23, 42],    // Level 0
                [30, 41, 59],    // Level 1
                [51, 65, 85],    // Level 2
                [71, 85, 105]    // Level 3
            ];
            const fontStyles = level < 2 ? 'bold' : (level === 2 ? 'bold' : 'normal');
            const fontSize = level === 0 ? 8.5 : (level === 1 ? 8 : (level === 2 ? 7.5 : 7.5));
            const fillColor = bgPalette[Math.min(level, 3)];
            const textColor = textPalette[Math.min(level, 3)];

            rows.push([
                { content: String(id), styles: { 
                    fontStyle: fontStyles, 
                    halign: 'center', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: `${indent}${name}`, styles: { 
                    fontStyle: fontStyles, 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(opDr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(opCr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(curDr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(curCr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(clsDr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }},
                { content: formatAmount(clsCr), styles: { 
                    halign: 'right', 
                    fillColor: fillColor, 
                    textColor: textColor,
                    fontSize: fontSize
                }}
            ]);

            if (level < 3) {
                toArray(data.subHeads || data.subSubHeads || data.accounts).forEach(child => {
                    addRows(child, level + 1);
                });
            }
        };

        hierarchyEntries.forEach(head => addRows(head, 0));

        autoTable(doc, {
            startY: 29,
            head: [[
                { content: 'ID', styles: { halign: 'center', cellWidth: 18 } },
                { content: 'Account Title / Hierarchy Section', styles: { halign: 'left', cellWidth: 95 } },
                { content: 'Open. Dr', styles: { halign: 'right', cellWidth: 24 } },
                { content: 'Open. Cr', styles: { halign: 'right', cellWidth: 24 } },
                { content: 'Cur. Dr', styles: { halign: 'right', cellWidth: 24 } },
                { content: 'Cur. Cr', styles: { halign: 'right', cellWidth: 24 } },
                { content: 'Close. Dr', styles: { halign: 'right', cellWidth: 25 } },
                { content: 'Close. Cr', styles: { halign: 'right', cellWidth: 25 } }
            ]],
            body: rows,
            theme: 'grid',
            headStyles: { 
                fillColor: [15, 23, 42], 
                textColor: [255, 255, 255], 
                fontSize: 8.5, 
                fontStyle: 'bold',
                cellPadding: 2.5
            },
            styles: { 
                fontSize: 7.5, 
                cellPadding: 1.8, 
                lineColor: [226, 232, 240],
                lineWidth: 0.2
            },
            margin: { left: 14, right: 14 }
        });

        const finalY = doc.lastAutoTable.finalY + 8;
        doc.setLineWidth(0.4);
        doc.setDrawColor(15, 23, 42); // ✅ Fixed: setColor to setDrawColor
        doc.line(14, finalY, pageWidth - 14, finalY);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text("GRAND TOTALS:", 14, finalY + 5);

        doc.text(`Op. Dr: ${formatAmount(tbData.summary?.totalOpeningDebit)}`, 48, finalY + 5);
        doc.text(`Op. Cr: ${formatAmount(tbData.summary?.totalOpeningCredit)}`, 90, finalY + 5);
        doc.text(`Cur. Dr: ${formatAmount(tbData.summary?.totalCurrentDebit)}`, 132, finalY + 5);
        doc.text(`Cur. Cr: ${formatAmount(tbData.summary?.totalCurrentCredit)}`, 174, finalY + 5);

        const closingY = finalY + 8;
        doc.setFillColor(241, 245, 249);
        doc.rect(14, closingY, pageWidth - 28, 8, 'F');
        doc.setFontSize(9);
        doc.text(`Closing Debit Total: ${formatAmount(tbData.summary?.totalClosingDebit)}`, 18, closingY + 5.5);
        doc.text(`Closing Credit Total: ${formatAmount(tbData.summary?.totalClosingCredit)}`, pageWidth / 2 + 15, closingY + 5.5);

        doc.save(`Trial_Balance_${tbDates.to}.pdf`);
    };

    const exportTrialBalanceExcel = () => {
        if (!tbData?.summary || !tbData.hierarchy) {
            return alert("No Trial Balance data available to export!");
        }

        const hierarchyEntries = toArray(tbData.hierarchy);
        if (hierarchyEntries.length === 0) {
            return alert("No Trial Balance data available to export!");
        }

        const excelData = [];
        excelData.push([COMPANY_INFO?.name || "UNITED MOTORS"]);
        excelData.push(["TRIAL BALANCE REPORT"]);
        excelData.push([tbDates.from ? `Period: ${tbDates.from} to ${tbDates.to}` : `As of: ${tbDates.to}`]);
        excelData.push([]); 

        excelData.push([
            "ID", 
            "Account Title / Hierarchy Section", 
            "Open. Dr", 
            "Open. Cr", 
            "Cur. Dr", 
            "Cur. Cr", 
            "Close. Dr", 
            "Close. Cr"
        ]);

        // ✅ RECURSIVE EXCEL GENERATION (CLEAN HIERARCHY INDENTATION WITHOUT LABELS)
        const addExcelRows = (data, level = 0) => {
            if (!data) return;

            const indent = '    '.repeat(level);
            const id = data.HeadID || data.SubHeadID || data.SubSubHeadID || data.id || '';
            const name = data.HeadName || data.SubHeadName || data.SubSubHeadName || data.name || data.title || '';

            const totals = data.totals || {};
            const opDr = totals.opDr || totals.opening_debit || data.opening_debit || 0;
            const opCr = totals.opCr || totals.opening_credit || data.opening_credit || 0;
            const curDr = totals.curDr || totals.current_debit || data.current_debit || 0;
            const curCr = totals.curCr || totals.current_credit || data.current_credit || 0;
            const clsDr = totals.clsDr || totals.closing_debit || data.closing_debit || 0;
            const clsCr = totals.clsCr || totals.closing_credit || data.closing_credit || 0;

            excelData.push([
                id,
                `${indent}${name}`,
                Number(opDr || 0),
                Number(opCr || 0),
                Number(curDr || 0),
                Number(curCr || 0),
                Number(clsDr || 0),
                Number(clsCr || 0)
            ]);

            if (level < 3) {
                toArray(data.subHeads || data.subSubHeads || data.accounts).forEach(child => {
                    addExcelRows(child, level + 1);
                });
            }
        };

        hierarchyEntries.forEach(head => addExcelRows(head, 0));

        excelData.push([]);
        excelData.push([
            "OVERALL GRAND TOTALS",
            "",
            Number(tbData.summary?.totalOpeningDebit || 0),
            Number(tbData.summary?.totalOpeningCredit || 0),
            Number(tbData.summary?.totalCurrentDebit || 0),
            Number(tbData.summary?.totalCurrentCredit || 0),
            Number(tbData.summary?.totalClosingDebit || 0),
            Number(tbData.summary?.totalClosingCredit || 0)
        ]);

        const worksheet = XLSX.utils.aoa_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Trial Balance");
        XLSX.writeFile(workbook, `Trial_Balance_${tbDates.to}.xlsx`);
    };

    // ═══════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════
    return (
        <div style={styles.container}>
            {/* --- REPORT TOGGLE BUTTONS --- */}
            <div style={styles.toggleBar}>
                <button 
                    onClick={() => setActiveReport('daybook')}
                    style={activeReport === 'daybook' ? styles.toggleBtnActive : styles.toggleBtn}
                >
                    <BookOpen size={18} style={{marginRight: '8px'}} />
                    Daily Report (Daybook)
                </button>
                <button 
                    onClick={() => setActiveReport('statement')}
                    style={activeReport === 'statement' ? styles.toggleBtnActive : styles.toggleBtn}
                >
                    <BarChart3 size={18} style={{marginRight: '8px'}} />
                    Account Statement
                </button>
                <button 
                    onClick={() => setActiveReport('trial_balance')}
                    style={activeReport === 'trial_balance' ? styles.toggleBtnActive : styles.toggleBtn}
                >
                    <FileText size={18} style={{marginRight: '8px'}} />
                    Trial Balance
                </button>
            </div>

            {/* ═════ DAYBOOK REPORT SECTION ═════ */}
            {activeReport === 'daybook' && (
                <div style={styles.reportCard}>
                    <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                        <h3 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '20px' }}>Daybook Report Generator</h3>
                        <p style={{ color: '#64748b', fontSize: '13px' }}>Dynamic Ledger with Running Balances (Dr/Cr)</p>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '25px' }}>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#334155' }}>From Date:</label>
                            <input 
                                type="date" 
                                value={daybookDates.start} 
                                onChange={(e) => setDaybookDates({...daybookDates, start: e.target.value})} 
                                style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }} 
                            />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#334155' }}>To Date:</label>
                            <input 
                                type="date" 
                                value={daybookDates.end} 
                                onChange={(e) => setDaybookDates({...daybookDates, end: e.target.value})} 
                                style={{ padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }} 
                            />
                        </div>
                    </div>

                    <button 
                        onClick={generateDaybookPDF} 
                        disabled={daybookLoading} 
                        style={{ width: '100%', padding: '13px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '15px' }}
                    >
                        {daybookLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />} 
                        Download Daybook PDF Report
                    </button>
                </div>
            )}

            {/* ═════ ACCOUNT STATEMENT SECTION ═════ */}
            {activeReport === 'statement' && (
                <>
                    <div style={styles.filterCard}>
                        <div style={{ flex: 2, position: 'relative' }} ref={dropdownRef}>
                            <label style={styles.label}>Account Search</label>
                            <div style={styles.searchWrapper}>
                                <input 
                                    style={styles.input} 
                                    placeholder="Search by Title or ID..." 
                                    value={searchTerm}
                                    onFocus={() => setIsOpen(true)}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search size={16} style={styles.searchIcon} />
                            </div>
                            {isOpen && options.length > 0 && (
                                <div style={styles.dropdown}>
                                    {options.map(acc => (
                                        <div key={acc.id} style={styles.dropdownItem} onClick={() => {
                                            setSelectedAccount(acc);
                                            setSearchTerm(acc.title);
                                            setIsOpen(false);
                                        }}>
                                            <div style={{fontWeight: 'bold'}}>{acc.title}</div>
                                            <div style={{fontSize: '11px', color: '#64748b'}}>ID: {acc.id} | {acc.SubHeadName}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={styles.label}>From Date</label>
                                <button onClick={handleSetOpeningDate} style={styles.linkBtn}>
                                    <RotateCcw size={10}/> Use Opening
                                </button>
                            </div>
                            <input type="date" style={styles.input} value={stmtDates.from} onChange={e => setStmtDates({ ...stmtDates, from: e.target.value })} />
                        </div>

                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>To Date</label>
                            <input type="date" style={styles.input} value={stmtDates.to} onChange={e => setStmtDates({ ...stmtDates, to: e.target.value })} />
                        </div>

                        <div style={{display:'flex', gap:'8px'}}>
                            <button onClick={fetchStatement} disabled={stmtLoading} style={styles.btnView}>
                                {stmtLoading ? <Loader2 className="animate-spin" size={18} /> : "View Report"}
                            </button>
                            {reportData.accountInfo && (
                                <button onClick={exportStatementPDF} style={styles.btnDownload}>
                                    <Download size={18} />
                                </button>
                            )}
                        </div>
                    </div>

                    {reportData.accountInfo && (
                        <div style={styles.tableCard}>
                            <div style={styles.tableHeader}>
                                <span>Statement: {reportData.accountInfo.title}</span>
                                <span style={{fontSize: '12px', fontWeight: 'normal'}}>ID: {reportData.accountInfo.id}</span>
                            </div>
                            <div style={{overflowX: 'auto'}}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Date</th>
                                            <th style={styles.th}>Description</th>
                                            <th style={styles.thRight}>Debit</th>
                                            <th style={styles.thRight}>Credit</th>
                                            <th style={styles.thRight}>Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
                                            <td colSpan="4" style={styles.td}>Opening Balance Brought Forward</td>
                                            <td style={styles.tdRight}>{formatBalance(reportData.openingBalance)}</td>
                                        </tr>
                                        {(() => {
                                            let runningBal = Number(reportData.openingBalance || 0);
                                            return reportData.transactions.map((t, i) => {
                                                runningBal += (Number(t.debit || 0) - Number(t.credit || 0));
                                                return (
                                                    <tr key={i} style={styles.tr}>
                                                        <td style={styles.td}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</td>
                                                        <td style={styles.td}>{t.description || ''}</td>
                                                        <td style={styles.tdRight}>{(t.debit || 0) > 0 ? formatAmount(t.debit) : '-'}</td>
                                                        <td style={styles.tdRight}>{(t.credit || 0) > 0 ? formatAmount(t.credit) : '-'}</td>
                                                        <td style={{ ...styles.tdRight, fontWeight: 'bold' }}>{formatBalance(runningBal)}</td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ═════ TRIAL BALANCE SECTION ═════ */}
            {activeReport === 'trial_balance' && (
                <>
                    <div style={styles.filterCard}>
                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>From Date (Optional)</label>
                            <input 
                                type="date" 
                                style={styles.input} 
                                value={tbDates.from} 
                                onChange={e => setTbDates({ ...tbDates, from: e.target.value })} 
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={styles.label}>To Date</label>
                            <input 
                                type="date" 
                                style={styles.input} 
                                value={tbDates.to} 
                                onChange={e => setTbDates({ ...tbDates, to: e.target.value })} 
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                            <button onClick={fetchTrialBalance} disabled={tbLoading} style={styles.btnView}>
                                {tbLoading ? <Loader2 className="animate-spin" size={18} /> : "View Trial Balance"}
                            </button>
                            {tbData.summary && (
                                <>
                                    <button onClick={exportTrialBalancePDF} style={styles.btnDownload} title="Download PDF Report">
                                        <Download size={16} /> PDF
                                    </button>
                                    <button onClick={exportTrialBalanceExcel} style={styles.btnExcel} title="Download Excel Spreadsheet">
                                        <FileSpreadsheet size={16} /> Excel
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {tbData.summary && (
                        <div style={styles.tableCard}>
                            <div style={styles.tableHeader}>
                                <span>Trial Balance Report</span>
                                <span style={{ fontSize: '12px' }}>
                                    {tbData.summary.isBalanced ? "✅ Balanced" : "❌ Unbalanced"}
                                </span>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...styles.th, width: '70px', textAlign: 'center' }}>ID</th>
                                            <th style={styles.th}>Account Title / Hierarchy Section</th>
                                            <th style={styles.thRight}>Open. Dr</th>
                                            <th style={styles.thRight}>Open. Cr</th>
                                            <th style={styles.thRight}>Cur. Dr</th>
                                            <th style={styles.thRight}>Cur. Cr</th>
                                            <th style={styles.thRight}>Close. Dr</th>
                                            <th style={styles.thRight}>Close. Cr</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {toArray(tbData.hierarchy).length === 0 ? (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                                                    No records found for the selected criteria.
                                                </td>
                                            </tr>
                                        ) : (
                                            toArray(tbData.hierarchy).map((head, idx) => (
                                                <TrialBalanceRow key={`head-${idx}`} data={head} level={0} />
                                            ))
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ backgroundColor: '#0f172a', color: '#fff', fontWeight: 'bold', fontSize: '13px' }}>
                                            <td colSpan="2" style={styles.td}>OVERALL GRAND TOTALS</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalOpeningDebit)}</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalOpeningCredit)}</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalCurrentDebit)}</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalCurrentCredit)}</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalClosingDebit)}</td>
                                            <td style={styles.tdRight}>{formatAmount(tbData.summary?.totalClosingCredit)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════
const styles = {
    container: { padding: '25px', backgroundColor: '#f1f5f9', minHeight: '100vh', fontFamily: 'Inter, sans-serif' },

    toggleBar: { 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '25px',
        background: '#fff',
        padding: '8px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        width: 'fit-content'
    },
    toggleBtn: { 
        padding: '12px 24px', 
        background: 'transparent', 
        color: '#64748b', 
        border: 'none', 
        borderRadius: '8px', 
        cursor: 'pointer', 
        fontWeight: '600', 
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.2s ease'
    },
    toggleBtnActive: { 
        padding: '12px 24px', 
        background: '#0f172a', 
        color: '#fff', 
        border: 'none', 
        borderRadius: '8px', 
        cursor: 'pointer', 
        fontWeight: '600', 
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)'
    },

    reportCard: { 
        padding: '40px', 
        maxWidth: '650px', 
        margin: '0 auto', 
        background: '#ffffff', 
        borderRadius: '12px', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        textAlign: 'center', 
        fontFamily: 'Arial, sans-serif' 
    },

    filterCard: { display: 'flex', gap: '20px', backgroundColor: '#fff', padding: '20px', borderRadius: '12px', alignItems: 'flex-end', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', marginBottom: '25px' },
    label: { fontSize: '11px', fontWeight: '700', color: '#1e293b', marginBottom: '6px', display: 'block', textTransform: 'uppercase' },
    searchWrapper: { position: 'relative' },
    searchIcon: { position: 'absolute', right: '12px', top: '12px', color: '#94a3b8' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', outline: 'none' },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 100, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: '250px', overflowY: 'auto', marginTop: '5px' },
    dropdownItem: { padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
    btnView: { padding: '10px 25px', backgroundColor: '#164e63', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    btnDownload: { padding: '10px 16px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' },
    btnExcel: { padding: '10px 16px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' },
    linkBtn: { background: 'none', border: 'none', color: '#be123c', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '800' },
    tableCard: { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
    tableHeader: { padding: '15px 20px', backgroundColor: '#164e63', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '12px 15px', textAlign: 'left', backgroundColor: '#f8fafc', fontSize: '12px', color: '#475569' },
    thRight: { padding: '12px 15px', textAlign: 'right', backgroundColor: '#f8fafc', fontSize: '12px', color: '#475569' },
    td: { padding: '10px 15px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' },
    tdRight: { padding: '10px 15px', fontSize: '13px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' },
    tr: { '&:hover': { backgroundColor: '#fdfdfd' } }
};

export default ReportsPanel;